// Hand-crafted 32x32 dark-fantasy SVG icons for spells, passives, gear slots, all 24 gear items, and chest art.
// Each icon uses inked silhouette contours (#0d0818), rich material tones, currentColor accents, and specular highlights.

import { gearBase, type ChestId, type GearSlot } from '../game/gear.ts';

const INK = '#0d0818';
const W = '#ffffff';
const CREAM = '#fff6dc';
const GOLD = '#f2be42';
const GOLD_DARK = '#9e6b14';
const STEEL = '#9fb0c7';
const STEEL_DARK = '#4d5b73';
const WOOD = '#6e4226';
const EMBER = '#ff5e2b';
const STORM = '#5ce1ff';
const VERDANT = '#49db7a';
const VOID = '#b86bff';
const RUBY = '#ff3864';

// ======================= 16 Spells =======================
const ICONS: Record<string, string> = {
  // Streaking crystalline comet spear with twin spiraling mana ribbons
  arcane_bolt: `
    <path d="M4 28C8 22 13 18 19 13" stroke="${INK}" stroke-width="5" stroke-linecap="round"/>
    <path d="M3.5 28.5C8.5 22 13.5 17.5 20 12" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"/>
    <path d="M6 21c3-4 8-5 13-4M11 26c1-5 2-10 6-13" fill="none" stroke="${STORM}" stroke-width="1.5" stroke-linecap="round" opacity=".85"/>
    <path d="M29.5 2.5L23 16.5l-3.2-4.3-4.3-3.2z" fill="${INK}" stroke="${INK}" stroke-linejoin="round" stroke-width="2"/>
    <path d="M29 3L22.6 15.8l-2.6-3.8-3.8-2.6z" fill="currentColor"/>
    <path d="M29 3L20 12l-3.8-2.6z" fill="${CREAM}" opacity=".75"/>
    <path d="M29 3l-6.4 12.8-2.6-3.8z" fill="${VOID}" opacity=".55"/>
    <circle cx="7" cy="25" r="1.4" fill="${CREAM}"/>
    <circle cx="14" cy="21" r="1.1" fill="${STORM}"/>`,

  // Cracked molten meteor core wrapped in roaring asymmetric flame tongues
  fireball: `
    <path d="M28 4c-5 1-9 3-13 6 2-3 5-5 8-7-6 2-12 5-16 10-3 3.5-4 8.5-1 12.5s8 4.5 12.5 1.5c5-3.5 7.5-9.5 9.5-16-2 2-4 3-6 3.5 3-3 5-6 6-10.5z" fill="${INK}" stroke="${INK}" stroke-width="1.6" stroke-linejoin="round"/>
    <path d="M27.5 4.5c-4.5 1-8.5 3.5-12 7 1.8-2.8 4-4.8 6.5-6.5-5.5 2-11 5-14.5 9.5-2.8 3.5-3.2 8-.5 11.2 2.8 3.2 7.5 3.5 11.5.5 4.2-3.2 6.8-8.5 8.5-14.5-1.8 1.5-3.5 2.4-5.2 2.8 2.8-2.6 4.8-5.8 5.7-10z" fill="${EMBER}"/>
    <path d="M22 9c-3 2-5.5 4.5-7 7.5 1.5-1 3.2-1.5 4.8-1.5-2.2 3.2-4.8 6-8 7.5-3 1.2-6-.2-6.8-3.2-.8-3 1.2-6.5 4.5-8.5 3.5-2.2 8-3.5 12.5-1.8z" fill="${GOLD}"/>
    <circle cx="11.5" cy="20.5" r="4.6" fill="${INK}"/>
    <circle cx="11.5" cy="20.5" r="3.8" fill="#ff3b1f"/>
    <path d="M9.2 18.8l2.4 1.2 2.2-1.4-.8 2.6 1.4 2-2.5-.4-1.8 1.5-.2-2.4-1.9-1.2z" fill="${CREAM}"/>
    <circle cx="24" cy="14" r="1.3" fill="${GOLD}"/>
    <circle cx="19" cy="5" r="1.1" fill="${EMBER}"/>`,

  // Asymmetric glacier burst: razor ice-crystals erupting from a fractured frozen ring
  frost_nova: `
    <ellipse cx="16" cy="24.5" rx="12" ry="4.2" fill="${INK}"/>
    <ellipse cx="16" cy="24.5" rx="10.8" ry="3.2" fill="#1b3b5c" stroke="${STORM}" stroke-width="1.2"/>
    <path d="M16 2L12.2 14.5 6 8.5l2.5 10.5L3 21.5l10 2.5h6l10-2.5-5.5-2.5L26 8.5l-6.2 6z" fill="${INK}" stroke="${INK}" stroke-width="1.8" stroke-linejoin="round"/>
    <path d="M16 3.2L13 15.5l3 7.5 3-7.5z" fill="${STORM}"/>
    <path d="M16 3.2L13 15.5l3 7.5z" fill="${W}" opacity=".85"/>
    <path d="M6.8 9.5l5.8 5.8-1.2 7.2-2.4-4.2z" fill="currentColor"/>
    <path d="M25.2 9.5l-5.8 5.8 1.2 7.2 2.4-4.2z" fill="#3da8db"/>
    <path d="M6.8 9.5l3.6 8.8-1.2-3zM25.2 9.5l-2.2 5.8 1.2 3z" fill="${W}" opacity=".7"/>
    <path d="M4.2 21.2l7.8.8-2.2 2.2zM27.8 21.2l-7.8.8 2.2 2.2z" fill="${W}" opacity=".8"/>
    <polygon points="16,13 18.4,18 16,22.8 13.6,18" fill="${W}"/>`,

  // Jagged forked high-voltage arc jumping between three conductive nodes
  chain_lightning: `
    <path d="M19 2L7 15h7.5L9 30l16-16.5h-7.8L22 2z" fill="${INK}" stroke="${INK}" stroke-width="2.2" stroke-linejoin="round"/>
    <path d="M5 7l5 4M27 24l-5.5-3.5M26 7l-5 3.5" stroke="${INK}" stroke-width="3.2" stroke-linecap="round"/>
    <path d="M5 7l5 4M27 24l-5.5-3.5M26 7l-5 3.5" stroke="${STORM}" stroke-width="1.6" stroke-linecap="round"/>
    <circle cx="4.5" cy="6.5" r="2.6" fill="${INK}"/><circle cx="4.5" cy="6.5" r="1.7" fill="${CREAM}"/>
    <circle cx="27" cy="24" r="2.6" fill="${INK}"/><circle cx="27" cy="24" r="1.7" fill="${STORM}"/>
    <circle cx="26.5" cy="6.5" r="2.2" fill="${INK}"/><circle cx="26.5" cy="6.5" r="1.4" fill="currentColor"/>
    <path d="M19 2.8L8 14.5h7.2L10 29l14-15h-7.2L21.4 2.8z" fill="currentColor"/>
    <path d="M19.5 4.5L10.5 14h5.6l-3.8 10.5 9.2-10h-5.4z" fill="${W}"/>`,

  // Three spectral lantern-wisps with trailing ectoplasmic flames orbiting a rune eye
  spirit_orbs: `
    <circle cx="16" cy="17" r="10.5" fill="none" stroke="${INK}" stroke-width="3"/>
    <circle cx="16" cy="17" r="10.5" fill="none" stroke="currentColor" stroke-width="1.3" stroke-dasharray="4 2.5" opacity=".75"/>
    <polygon points="16,13 19.5,17 16,21 12.5,17" fill="${INK}"/>
    <polygon points="16,14.2 18.3,17 16,19.8 13.7,17" fill="${VOID}"/>
    <path d="M16 1.8c-2.5 2.4-4 4.6-4 6.7a4 4 0 0 0 8 0c0-2.1-1.5-4.3-4-6.7z" fill="${INK}" stroke="${INK}" stroke-width="1.2"/>
    <path d="M16 2.8c-2 2-3.2 3.8-3.2 5.6a3.2 3.2 0 0 0 6.4 0c0-1.8-1.2-3.6-3.2-5.6z" fill="currentColor"/>
    <circle cx="16" cy="8.8" r="1.6" fill="${W}"/>
    <path d="M4.2 18.2c0-2.4 1.8-4.5 4.2-5.6-.4 2 .2 3.4 1.4 4.5a3.6 3.6 0 1 1-5.6 1.1z" fill="${INK}" stroke="${INK}" stroke-width="1.2"/>
    <path d="M4.8 18.5a3.1 3.1 0 1 0 4.5-1.2c-1-.9-1.5-2-1.2-3.6-2 1-3.3 2.8-3.3 4.8z" fill="currentColor"/>
    <circle cx="7.6" cy="19.6" r="1.5" fill="${W}"/>
    <path d="M27.8 18.2c0-2.4-1.8-4.5-4.2-5.6.4 2-.2 3.4-1.4 4.5a3.6 3.6 0 1 0 5.6 1.1z" fill="${INK}" stroke="${INK}" stroke-width="1.2"/>
    <path d="M27.2 18.5a3.1 3.1 0 1 1-4.5-1.2c1-.9 1.5-2 1.2-3.6 2 1 3.3 2.8 3.3 4.8z" fill="currentColor"/>
    <circle cx="24.4" cy="19.6" r="1.5" fill="${W}"/>`,

  // Corrosive serpent-fang vial spilling a bubbling pool of acid mire
  venom_pool: `
    <ellipse cx="16" cy="24.5" rx="13" ry="5" fill="${INK}"/>
    <ellipse cx="16" cy="24.5" rx="11.6" ry="3.8" fill="#1f6b3b"/>
    <ellipse cx="15.5" cy="24.2" rx="9.2" ry="2.6" fill="currentColor"/>
    <circle cx="10" cy="24" r="1.8" fill="${CREAM}" opacity=".85"/>
    <circle cx="20.5" cy="24.8" r="1.4" fill="${VERDANT}"/>
    <circle cx="15" cy="20" r="2.2" fill="${INK}"/><circle cx="15" cy="20" r="1.5" fill="currentColor"/>
    <path d="M9 3c1 5 2.5 9.5 4.5 13.5L15 9.5 16.5 3zM23 3c-1 5-2.5 9.5-4.5 13.5L17 9.5 15.5 3z" fill="${INK}" stroke="${INK}" stroke-width="1.6" stroke-linejoin="round"/>
    <path d="M9.8 3.6c.9 4.2 2.1 8 3.8 11.5L14.8 9V3.6zM22.2 3.6c-.9 4.2-2.1 8-3.8 11.5L17.2 9V3.6z" fill="${CREAM}"/>
    <path d="M12 11.5l1.6 3.6.8-3.2zM20 11.5l-1.6 3.6-.8-3.2z" fill="currentColor"/>
    <path d="M16 14.5c-1.4 2-2.2 3.4-2.2 4.6a2.2 2.2 0 0 0 4.4 0c0-1.2-.8-2.6-2.2-4.6z" fill="currentColor"/>`,

  // Celestial winged valkyrie lance piercing through a golden halo
  radiant_lance: `
    <ellipse cx="17" cy="15" rx="9.5" ry="4.2" transform="rotate(-45 17 15)" fill="none" stroke="${INK}" stroke-width="3.2"/>
    <ellipse cx="17" cy="15" rx="9.5" ry="4.2" transform="rotate(-45 17 15)" fill="none" stroke="${GOLD}" stroke-width="1.5"/>
    <path d="M3.5 28.5L22 10" stroke="${INK}" stroke-width="4.6" stroke-linecap="round"/>
    <path d="M29.5 2.5L21.5 16l-2.8-2.7-2.7-2.8z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M13 13l-6 1.5 4.5-6 4.5-1zM19 19l-1.5 6 6-4.5 1-4.5z" fill="${INK}" stroke="${INK}" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M13 13l-5.2 1.2 3.8-5.2 3.8-.8zM19 19l-1.2 5.2 5.2-3.8.8-3.8z" fill="${GOLD}"/>
    <path d="M4 28L21.5 10.5" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/>
    <path d="M29 3L21.2 15.2l-2.4-2.4-2.4-2.4z" fill="currentColor"/>
    <path d="M29 3L18.8 13.2l-2.4-2.4z" fill="${W}"/>`,

  // Twin crossing razor-gale crescent scythes around a storm eye
  wind_scythe: `
    <path d="M25 5A12.5 12.5 0 1 0 26 24.5 9.2 9.2 0 0 1 25 5z" fill="${INK}" stroke="${INK}" stroke-width="1.8" stroke-linejoin="round"/>
    <path d="M24.5 5.6A11.8 11.8 0 1 0 25.4 24 8.8 8.8 0 0 1 24.5 5.6z" fill="currentColor"/>
    <path d="M18.5 6.2A10 10 0 0 0 7.5 19.5 8.2 8.2 0 0 1 18.5 6.2z" fill="${W}" opacity=".85"/>
    <path d="M28 11A8.5 8.5 0 0 1 13.5 21.5 6.4 6.4 0 0 0 25.5 10z" fill="${INK}" stroke="${INK}" stroke-width="1.4"/>
    <path d="M27.5 11.4A8 8 0 0 1 14 21a6 6 0 0 0 11.5-10.2z" fill="${STORM}"/>
    <circle cx="17.5" cy="15.5" r="2.2" fill="${INK}"/><circle cx="17.5" cy="15.5" r="1.2" fill="${W}"/>`,

  // Three jagged basalt-and-amber stalagmites smashing through cracked ground
  earth_spike: `
    <path d="M2 27.5L6.5 12l4 5.5L16.5 2.5l5.5 15 3.5-6.5L30 27.5z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M3 27L6.8 13.5l3.8 6L7.5 27z" fill="#8c6239"/>
    <path d="M29 27l-3.6-14.5-3.2 6L24.5 27z" fill="#754f2b"/>
    <path d="M9.5 27L16.5 3.8 23 27z" fill="currentColor"/>
    <path d="M9.5 27L16.5 3.8 15.2 27z" fill="${GOLD}" opacity=".75"/>
    <path d="M16.5 3.8L13.5 18l3-3.5z" fill="${CREAM}" opacity=".9"/>
    <path d="M1.5 27.5h29" stroke="${INK}" stroke-width="3" stroke-linecap="round"/>
    <path d="M2.5 27.5h27" stroke="${GOLD_DARK}" stroke-width="1.4" stroke-linecap="round"/>`,

  // Curved assassin's kris blade with a crimson blood-fuller and shadow echo
  shadow_dagger: `
    <path d="M22 3c-5 4-9 9.5-11 15.5L7.5 16l-2 3.5 4.5 3-3.5 3.5 2.5 2.5 3.5-3.5 3 4.5 3.5-2-2.5-3.5C22.5 22 28 18 29 10z" fill="${INK}" stroke="${INK}" stroke-width="1.8" stroke-linejoin="round"/>
    <path d="M18.5 3.5C14 7 10.5 11.5 9 16.5l3.2 1.8C14 13.5 17 9 21 5z" fill="${VOID}" opacity=".55"/>
    <path d="M28 5c-6.5 3.5-11.5 9-14 15.5l3.5 3.5C24 21.5 27.5 14.5 28 5z" fill="${STEEL}"/>
    <path d="M28 5c-5 3-9.5 7.8-12.2 13.8l1.8 1.8C21 15 25 10.2 28 5z" fill="${W}"/>
    <path d="M25.5 8.5c-3.2 2.8-6 6.2-7.8 10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
    <path d="M8.5 17l9.5 9.5-2.2 1.4-2.8-4-4 4-1.8-1.8 4-4-4-2.8z" fill="currentColor" stroke="${INK}" stroke-width="1"/>
    <circle cx="13.2" cy="21.8" r="1.6" fill="${RUBY}"/>`,

  // Tilted event-horizon singularity with glowing accretion ring and bent starlight
  gravity_well: `
    <ellipse cx="16" cy="16" rx="13.5" ry="6.2" transform="rotate(-24 16 16)" fill="${INK}" stroke="${INK}" stroke-width="2"/>
    <ellipse cx="16" cy="16" rx="12.5" ry="5" transform="rotate(-24 16 16)" fill="none" stroke="currentColor" stroke-width="2.6"/>
    <ellipse cx="16" cy="16" rx="9.5" ry="3.4" transform="rotate(-24 16 16)" fill="none" stroke="${CREAM}" stroke-width="1.3"/>
    <path d="M7 9c4-4 11-4.5 15.5-.5M25 23c-4 4-11 4.5-15.5.5" fill="none" stroke="${VOID}" stroke-width="1.6" stroke-linecap="round"/>
    <circle cx="16" cy="16" r="5.6" fill="${INK}" stroke="currentColor" stroke-width="1.5"/>
    <path d="M13 13.8a3.8 3.8 0 0 1 5.2-.4" fill="none" stroke="${W}" stroke-width="1.3" stroke-linecap="round"/>
    <circle cx="5.5" cy="11" r="1.3" fill="${CREAM}"/>
    <circle cx="26.5" cy="21" r="1.3" fill="${CREAM}"/>`,

  // Blazing solar crest with eight asymmetric curved flame tongues and sun-shield core
  sunfire_aura: `
    <path d="M16 1.5l3.2 5.2 5.8-2.2-1.2 6 5.7 2.3-4.6 4.2 3.6 5-5.8 1.2.2 6-5-3.5-3.8 4.8-2.2-5.7-5.8 1.6 1.8-5.8-5.2-3.2 5-3.8-2-5.6 5.8 1.2 2.2-5.7 3.5 4.8z" fill="${INK}" stroke="${INK}" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M16 2.5l2.8 4.8 5.2-1.8-1 5.4 5 2-4 3.8 3.2 4.4-5.2 1 .2 5.2-4.4-3-3.4 4.2-2-5-5.2 1.4 1.6-5.2-4.6-2.8 4.4-3.4-1.8-5 5.2 1 2-5 3 4.2z" fill="${EMBER}"/>
    <circle cx="16" cy="16" r="8.2" fill="${INK}"/>
    <circle cx="16" cy="16" r="7" fill="currentColor"/>
    <polygon points="16,9.5 18.2,13.8 22.5,16 18.2,18.2 16,22.5 13.8,18.2 9.5,16 13.8,13.8" fill="${CREAM}"/>
    <circle cx="16" cy="16" r="2.6" fill="${EMBER}"/>`,

  // Asymmetric cluster of three faceted prism crystals bursting from a geode base
  crystal_shards: `
    <path d="M16 2l5.2 7.5-1.8 13.5 6.8-9.5 3.8 4.5-6.2 11H8.2L2 18l3.8-4.5 6.8 9.5L10.8 9.5z" fill="${INK}" stroke="${INK}" stroke-width="1.8" stroke-linejoin="round"/>
    <polygon points="3.2,17.8 6,14.5 12.2,23 9,27.8" fill="#7a42cc"/>
    <polygon points="28.8,17.8 26,14.5 19.8,23 23,27.8" fill="#7a42cc"/>
    <polygon points="6,14.5 9.5,16.5 12.2,23" fill="${STORM}" opacity=".8"/>
    <polygon points="26,14.5 22.5,16.5 19.8,23" fill="${STORM}" opacity=".8"/>
    <polygon points="16,3.2 20.2,9.5 18.2,27.8 13.8,27.8 11.8,9.5" fill="currentColor"/>
    <polygon points="16,3.2 11.8,9.5 13.8,27.8 16,27.8" fill="${W}" opacity=".75"/>
    <polygon points="16,3.2 20.2,9.5 16,13.5 11.8,9.5" fill="${CREAM}"/>`,

  // Crimson vampire-fangs & swirling blood-tendrils feeding a faceted ruby droplet
  blood_siphon: `
    <path d="M6 4c0 6 4.5 8.5 10 11 5.5-2.5 10-5 10-11" fill="none" stroke="${INK}" stroke-width="4" stroke-linecap="round"/>
    <path d="M6 4c0 6 4.5 8.5 10 11 5.5-2.5 10-5 10-11" fill="none" stroke="${RUBY}" stroke-width="2" stroke-linecap="round"/>
    <path d="M16 9.5C11.5 15 8.5 19 8.5 23a7.5 7.5 0 0 0 15 0c0-4-3-8-7.5-13.5z" fill="${INK}" stroke="${INK}" stroke-width="1.8" stroke-linejoin="round"/>
    <path d="M16 11c-3.8 4.8-6.4 8.2-6.4 11.8a6.4 6.4 0 0 0 12.8 0c0-3.6-2.6-7-6.4-11.8z" fill="currentColor"/>
    <path d="M16 11c-3.8 4.8-6.4 8.2-6.4 11.8a6.4 6.4 0 0 0 4.8 6.2c-2-2.2-2.2-6.5 1.6-18z" fill="#7a0b2b"/>
    <path d="M13 21.5a3.5 3.5 0 0 0 3 4.2" fill="none" stroke="${W}" stroke-width="1.6" stroke-linecap="round"/>
    <circle cx="8" cy="9" r="1.5" fill="${RUBY}"/>
    <circle cx="24" cy="9" r="1.5" fill="${RUBY}"/>`,

  // Hexagonal floor-ward inscribed with an glowing elder bind-rune and corner nodes
  rune_trap: `
    <polygon points="16,2 28.5,9 28.5,23 16,30 3.5,23 3.5,9" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <polygon points="16,3.5 27,9.8 27,22.2 16,28.5 5,22.2 5,9.8" fill="#1c1030" stroke="currentColor" stroke-width="2"/>
    <polygon points="16,7 24,11.5 24,20.5 16,25 8,20.5 8,11.5" fill="none" stroke="${GOLD}" stroke-width="1.2" opacity=".8"/>
    <path d="M16 7.5v17M11 11l5 4.5 5-4.5M11 20.5l5-4.5 5 4.5" fill="none" stroke="${CREAM}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="16" cy="3.5" r="1.6" fill="${CREAM}"/>
    <circle cx="27" cy="9.8" r="1.6" fill="currentColor"/>
    <circle cx="27" cy="22.2" r="1.6" fill="currentColor"/>
    <circle cx="16" cy="28.5" r="1.6" fill="${CREAM}"/>
    <circle cx="5" cy="22.2" r="1.6" fill="currentColor"/>
    <circle cx="5" cy="9.8" r="1.6" fill="currentColor"/>`,

  // Towering curling ocean-breaker with clawing whitecap foam
  tidal_wave: `
    <path d="M2.5 26.5C4 15 10.5 5.5 20.5 5.5c4.5 0 7.5 2.5 8.5 5.8-2.2-1.4-4.8-1.4-6.8.2-2.6 2-2.4 6 .8 7.5 2 .8 4.2.6 6-.6-1.8 4.8-6.5 8.1-13 8.1z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M3.5 26C5 15.5 11 6.8 20.2 6.8c3.8 0 6.4 1.8 7.4 4.4-2-.9-4.4-.8-6.2.6-3 2.4-2.8 7 .6 8.8 1.8.9 3.8.9 5.5.1-1.8 3.6-5.8 5.3-11.5 5.3z" fill="currentColor"/>
    <path d="M6.5 24c1.8-7.5 6.5-13.5 13.5-14.2-3.5 2.2-5.5 6.2-4.8 10.2.5 2.8 2.8 4.8 6 5.2-3.5 1.2-9.5.8-14.7-1.2z" fill="#16528e" opacity=".7"/>
    <path d="M18.5 6.8c3.8 0 6.6 1.8 7.6 4.6-2-.9-4.2-.8-6 .6-1.5 1.2-2.2 2.8-2 4.5-1.6-2.4-1.4-6.2.4-9.7z" fill="${W}"/>
    <path d="M2 28.5c3.5 0 4.5-2 7-2s3.5 2 7 2 4.5-2 7-2 3.5 2 7 2" fill="none" stroke="${STORM}" stroke-width="2" stroke-linecap="round"/>
    <circle cx="27.5" cy="6.5" r="1.5" fill="${W}"/>
    <circle cx="24" cy="4" r="1.1" fill="${W}"/>`,
};

// ======================= 18 Passives & Grimoire =======================
const PASSIVE_ICONS: Record<string, string> = {
  // Runed broadsword with golden crossguard and ruby pommel
  might: `
    <path d="M16 2l4 5.5v12.5h5v3.5h-6.8v4.5a2.2 2.2 0 1 1-4.4 0v-4.5H7V20h5V7.5z" fill="${INK}" stroke="${INK}" stroke-width="1.8" stroke-linejoin="round"/>
    <polygon points="16,3 19,7.5 19,20 13,20 13,7.5" fill="currentColor"/>
    <polygon points="16,3 13,7.5 13,20 16,20" fill="${W}" opacity=".65"/>
    <rect x="8" y="20" width="16" height="2.6" rx="1" fill="${GOLD}"/>
    <rect x="14.5" y="22.6" width="3" height="4.2" fill="${WOOD}"/>
    <circle cx="16" cy="28" r="1.8" fill="${RUBY}"/>`,

  // Winged brass chronometer with lightning hand
  quickening: `
    <circle cx="16" cy="17" r="11" fill="${INK}" stroke="${INK}" stroke-width="1.6"/>
    <circle cx="16" cy="17" r="9.6" fill="#18122b" stroke="currentColor" stroke-width="2.2"/>
    <rect x="13.5" y="3" width="5" height="3" rx="1" fill="${GOLD}" stroke="${INK}" stroke-width="1.2"/>
    <path d="M16 10v7l5 3" fill="none" stroke="${CREAM}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="16" cy="17" r="1.8" fill="${GOLD}"/>
    <path d="M3 12h4M2 17h3.5M3 22h4" stroke="${STORM}" stroke-width="1.8" stroke-linecap="round"/>`,

  // Four-way arcane astrolabe expanding outward from a glowing core
  expansion: `
    <circle cx="16" cy="16" r="9.5" fill="none" stroke="${INK}" stroke-width="3.2"/>
    <circle cx="16" cy="16" r="9.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-dasharray="3.5 2.5"/>
    <path d="M16 2l4 5h-8zM16 30l4-5h-8zM2 16l5-4v8zM30 16l-5-4v8z" fill="${INK}" stroke="${INK}" stroke-width="1.2" stroke-linejoin="round"/>
    <path d="M16 3l3 4h-6zM16 29l3-4h-6zM3 16l4-3v6zM29 16l-4-3v6z" fill="currentColor"/>
    <circle cx="16" cy="16" r="4.8" fill="${INK}"/>
    <circle cx="16" cy="16" r="3.6" fill="${CREAM}"/>`,

  // Twin overlapping comet-spheres with a glowing duplication prism
  echo: `
    <circle cx="11" cy="18" r="7.2" fill="${INK}"/>
    <circle cx="11" cy="18" r="5.8" fill="currentColor" opacity=".55"/>
    <circle cx="20" cy="14" r="7.8" fill="${INK}"/>
    <circle cx="20" cy="14" r="6.4" fill="currentColor"/>
    <circle cx="18" cy="12" r="2.2" fill="${W}"/>
    <path d="M6 4v6M3 7h6" stroke="${INK}" stroke-width="3.4" stroke-linecap="round"/>
    <path d="M6 4v6M3 7h6" stroke="${CREAM}" stroke-width="1.8" stroke-linecap="round"/>`,

  // Hermes-style winged leather strider boot
  boots: `
    <path d="M8 5h8v10l9 4.5a3.5 3.5 0 0 1 2 3.2V26H6v-5l2-3z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M9 6h6v9.5l8.8 4.4c1 .5 1.6 1.5 1.6 2.6V25H7v-3.6l2-2.8z" fill="currentColor"/>
    <path d="M14 9c4-3.5 9-4.5 13-3-2.5 1.2-4.5 2.6-5.5 4.2 2-.2 3.8.2 5 1-3 .5-6 1.8-8.5 3.5z" fill="${CREAM}" stroke="${INK}" stroke-width="1" stroke-linejoin="round"/>
    <rect x="6" y="25" width="20" height="2.4" rx="1" fill="${GOLD}"/>`,

  // Faceted ruby heart set in a golden filigree mount
  heart: `
    <path d="M16 28.5S4 20.5 4 11.5A6.8 6.8 0 0 1 16 7a6.8 6.8 0 0 1 12 4.5c0 9-12 17-12 17z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M16 27S5.5 19.8 5.5 11.6A5.6 5.6 0 0 1 16 8.2a5.6 5.6 0 0 1 10.5 3.4C26.5 19.8 16 27 16 27z" fill="currentColor"/>
    <polygon points="16,11 21,14.5 16,24.5 11,14.5" fill="${RUBY}"/>
    <polygon points="16,11 11,14.5 16,17.5" fill="${W}" opacity=".75"/>`,

  // Polarized U-lodestone magnet with red/blue pole caps and sparks
  lode: `
    <path d="M7 4v11a9 9 0 0 0 18 0V4h-5.5v11a3.5 3.5 0 0 1-7 0V4z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M7.8 4.8v10.2a8.2 8.2 0 0 0 16.4 0V4.8h-4v10.2a4.2 4.2 0 0 1-8.4 0V4.8z" fill="currentColor"/>
    <rect x="7.8" y="4.8" width="4" height="4.5" fill="${CREAM}"/>
    <rect x="20.2" y="4.8" width="4" height="4.5" fill="${CREAM}"/>
    <path d="M13 3l2 3-2 2M19 3l-2 3 2 2" fill="none" stroke="${STORM}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,

  // Open arcane grimoire with gold corner clasps and rising rune spark
  tome: `
    <path d="M16 8C12.5 5.8 7.8 5.2 3 6.2v18.5c4.8-1 9.5-.4 13 1.8 3.5-2.2 8.2-2.8 13-1.8V6.2C24.2 5.2 19.5 5.8 16 8z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M16 9c-3.2-2-7.5-2.5-11.8-1.6v15.8c4.3-.9 8.6-.4 11.8 1.6 3.2-2 7.5-2.5 11.8-1.6V7.4C23.5 6.5 19.2 7 16 9z" fill="currentColor"/>
    <path d="M16 8.5v17" stroke="${INK}" stroke-width="2"/>
    <path d="M6.5 12h6.5M6.5 15.5h6.5M6.5 19h5M19 12h6.5M19 15.5h6.5M19 19h5" stroke="${CREAM}" stroke-width="1.4" stroke-linecap="round"/>
    <polygon points="16,2 17.5,5 20.5,6.5 17.5,8 16,11 14.5,8 11.5,6.5 14.5,5" fill="${GOLD}"/>`,

  // Heavy heraldic kite shield with steel boss and gold cross
  iron: `
    <path d="M16 2.5L27.5 6.8v8.4c0 7.2-4.8 12.4-11.5 14.8C9.3 27.6 4.5 22.4 4.5 15.2V6.8z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M16 4L26 7.8v7.4c0 6.2-4.1 10.8-10 13-5.9-2.2-10-6.8-10-13V7.8z" fill="currentColor"/>
    <path d="M16 4L6 7.8v7.4c0 6.2 4.1 10.8 10 13z" fill="${W}" opacity=".28"/>
    <path d="M16 5.5v21M7.5 13.5h17" stroke="${GOLD}" stroke-width="2"/>
    <circle cx="16" cy="13.5" r="2.6" fill="${CREAM}" stroke="${INK}" stroke-width="1.2"/>`,

  // Layered sylvan lotus flower with a glowing golden pollen heart
  bloom: `
    <path d="M16 3c3 4 4.2 8 3 12 3.5-2.5 7.5-2.5 10.5.5-2.5 4-6.5 6.5-10.5 6 1.5 3.5 0 6.5-3 7.5-3-1-4.5-4-3-7.5-4 .5-8-2-10.5-6 3-3 7-3 10.5-.5-1.2-4 0-8 3-12z" fill="${INK}" stroke="${INK}" stroke-width="1.8" stroke-linejoin="round"/>
    <path d="M6.5 15.5c2.8-2.2 6.2-2 9.5.5-3.3 2.5-6.7 2.7-9.5-.5zM25.5 15.5c-2.8-2.2-6.2-2-9.5.5 3.3 2.5 6.7 2.7 9.5-.5z" fill="#2ea85c"/>
    <path d="M16 4.5c2.6 3.5 3.5 7.2 2.2 11.2L16 19l-2.2-3.3C12.5 11.7 13.4 8 16 4.5z" fill="currentColor"/>
    <path d="M10 11c2.5 1 4.5 3.5 6 7-3.5-.5-6-2.8-6-7zM22 11c-2.5 1-4.5 3.5-6 7 3.5-.5 6-2.8 6-7z" fill="${CREAM}" opacity=".75"/>
    <circle cx="16" cy="18" r="3.2" fill="${GOLD}" stroke="${INK}" stroke-width="1.2"/>`,

  // Twin-pillar brass hourglass with trickling golden sand
  chrono: `
    <path d="M6 3h20v3.5H23v3c0 3.2-2.8 5.2-4.8 6.5 2 1.3 4.8 3.3 4.8 6.5v3h3V29H6v-3.5h3v-3c0-3.2 2.8-5.2 4.8-6.5-2-1.3-4.8-3.3-4.8-6.5v-3H6z" fill="${INK}" stroke="${INK}" stroke-width="1.6" stroke-linejoin="round"/>
    <path d="M10.5 6.5h11v3c0 2.6-3.2 4.6-5.5 5.8-2.3-1.2-5.5-3.2-5.5-5.8zM10.5 25.5h11v-3c0-2.6-3.2-4.6-5.5-5.8-2.3 1.2-5.5 3.2-5.5 5.8z" fill="#1b1430" stroke="currentColor" stroke-width="1.4"/>
    <path d="M12 9h8c-.6 1.8-2.4 3.2-4 4.2-1.6-1-3.4-2.4-4-4.2zM11.5 24.5c.8-2.2 2.6-3.6 4.5-4.4 1.9.8 3.7 2.2 4.5 4.4z" fill="${GOLD}"/>
    <rect x="6.8" y="3.6" width="18.4" height="2.4" rx="1" fill="currentColor"/>
    <rect x="6.8" y="26" width="18.4" height="2.4" rx="1" fill="currentColor"/>`,

  // Stamped sovereign gold coins with a four-point star emblem
  fortune: `
    <circle cx="20.5" cy="11.5" r="8" fill="${INK}"/>
    <circle cx="20.5" cy="11.5" r="6.6" fill="${GOLD_DARK}"/>
    <circle cx="13.5" cy="18.5" r="9.5" fill="${INK}"/>
    <circle cx="13.5" cy="18.5" r="8.1" fill="currentColor"/>
    <circle cx="13.5" cy="18.5" r="6.2" fill="none" stroke="${CREAM}" stroke-width="1.2"/>
    <polygon points="13.5,13 15.2,16.8 19,18.5 15.2,20.2 13.5,24 11.8,20.2 8,18.5 11.8,16.8" fill="${CREAM}"/>`,

  // Cut triangular crystal prism dispersing a light beam into three rays
  prism: `
    <path d="M2 15l9-2M21 11l8-3.5M22 15.5l8 .5M21 20l8 4" stroke="${INK}" stroke-width="3.6" stroke-linecap="round"/>
    <path d="M2 15l9-2" stroke="${W}" stroke-width="2" stroke-linecap="round"/>
    <path d="M21 11l8-3.5" stroke="${RUBY}" stroke-width="2" stroke-linecap="round"/>
    <path d="M22 15.5l8 .5" stroke="${GOLD}" stroke-width="2" stroke-linecap="round"/>
    <path d="M21 20l8 4" stroke="${STORM}" stroke-width="2" stroke-linecap="round"/>
    <polygon points="15.5,4 25.5,25.5 5.5,25.5" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <polygon points="15.5,5.5 24,24.2 7,24.2" fill="currentColor"/>
    <polygon points="15.5,5.5 15.5,24.2 7,24.2" fill="${W}" opacity=".55"/>`,

  // Ornate ritual blood-chalice overflowing with a crimson droplet
  pact: `
    <path d="M7 4h18c0 7.5-3.6 12-9 13v7h5.5v3.5h-11V24H16v-7C10.6 16 7 11.5 7 4z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M8.5 5.2h15c-.2 6-3.2 9.8-7.5 10.6-4.3-.8-7.3-4.6-7.5-10.6z" fill="currentColor"/>
    <path d="M9.5 5.2h13c-.4 2.2-3 3.4-6.5 3.4s-6.1-1.2-6.5-3.4z" fill="${RUBY}"/>
    <rect x="14.5" y="16" width="3" height="8" fill="${GOLD}"/>
    <rect x="10.5" y="24" width="11" height="2.5" rx="1" fill="${GOLD}"/>
    <path d="M25.5 14c-1.4 2-2.2 3.5-2.2 4.6a2.2 2.2 0 1 0 4.4 0c0-1.1-.8-2.6-2.2-4.6z" fill="${RUBY}" stroke="${INK}" stroke-width="1.2"/>`,

  // Standing weathered monolith carved with a glowing Ansuz/Odal rune
  runestone: `
    <polygon points="10,3 22,3 26.5,10 25,27 16,30 7,27 5.5,10" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <polygon points="10.8,4.2 21.2,4.2 25,10.5 23.8,26 16,28.6 8.2,26 7,10.5" fill="currentColor"/>
    <polygon points="10.8,4.2 15.5,4.2 11,14 7,10.5" fill="${W}" opacity=".28"/>
    <path d="M13 8.5v15M13 11l6.5 4.2-6.5 4.2M13 15.2l6.5 4.5" fill="none" stroke="${CREAM}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>`,

  // Crescent moon cradling a luminous iridescent pearl
  pearl: `
    <path d="M19 3A13 13 0 1 0 28.5 23 10.2 10.2 0 0 1 19 3z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M18.2 4A12 12 0 1 0 27.2 22.5 9.6 9.6 0 0 1 18.2 4z" fill="currentColor"/>
    <circle cx="21" cy="14.5" r="4.8" fill="${INK}"/>
    <circle cx="21" cy="14.5" r="3.6" fill="${CREAM}"/>
    <circle cx="19.8" cy="13.2" r="1.2" fill="${W}"/>`,

  // Twin curved fate-arrows orbiting a carved bone die
  reroll: `
    <path d="M25.5 11A10.5 10.5 0 0 0 7 9.5M6.5 21A10.5 10.5 0 0 0 25 22.5" fill="none" stroke="${INK}" stroke-width="4.2" stroke-linecap="round"/>
    <path d="M25.5 11A10.5 10.5 0 0 0 7 9.5M6.5 21A10.5 10.5 0 0 0 25 22.5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/>
    <path d="M26.5 4.5v7h-7M5.5 27.5v-7h7" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
    <rect x="11.5" y="11.5" width="9" height="9" rx="2" transform="rotate(45 16 16)" fill="${CREAM}" stroke="${INK}" stroke-width="1.6"/>
    <circle cx="16" cy="16" r="1.4" fill="${INK}"/>`,

  // Rising phoenix with outspread flame wings and tail crest
  revive: `
    <path d="M16 3l2.5 4.5C22 5 26.5 4.5 30 7c-2 4.5-5 8-9 9.5 3 1.5 5 4 5.5 7-3.5-.5-6.2-2-8-4.2L16 29.5l-2.5-10.2c-1.8 2.2-4.5 3.7-8 4.2.5-3 2.5-5.5 5.5-7-4-1.5-7-5-9-9.5 3.5-2.5 8-2 11.5.5z" fill="${INK}" stroke="${INK}" stroke-width="1.8" stroke-linejoin="round"/>
    <path d="M16 4.5l2.2 4.2c3.2-2.2 7.2-2.6 10.4-.5-1.8 3.8-4.5 6.8-8.2 8.2 2.6 1.2 4.2 3.2 4.6 5.6-2.8-.4-5.2-1.8-6.8-3.8L16 27.5l-2.2-9.3c-1.6 2-4 3.4-6.8 3.8.4-2.4 2-4.4 4.6-5.6-3.7-1.4-6.4-4.4-8.2-8.2 3.2-2.1 7.2-1.7 10.4.5z" fill="currentColor"/>
    <polygon points="16,6.5 18.5,13 16,23 13.5,13" fill="${CREAM}"/>`,
};

// ======================= 6 Gear Slot Emblems (empty slots & corner badges) =======================
const GEAR_ICONS: Record<GearSlot, string> = {
  staff: `
    <path d="M5 27L19.5 12.5" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
    <path d="M17.5 10.5c-2-3.5.5-7.8 4.8-8 .3 2.2 1.8 3.2 4 3-.5 4.2-4.5 6.8-8.8 5z" fill="currentColor" opacity=".7"/>
    <circle cx="22.5" cy="9.5" r="4.2" fill="currentColor"/>
    <circle cx="21.5" cy="8.5" r="1.5" fill="${W}"/>`,
  hat: `
    <path d="M16 3L9 19.5l-6 3.2c6.2 3 19.8 3 26 0l-6-3.2z" fill="currentColor"/>
    <path d="M9.5 19c4.2 1.4 8.8 1.4 13 0" fill="none" stroke="${W}" stroke-width="1.8" stroke-linecap="round"/>`,
  robe: `
    <path d="M11 3.5h10l2.8 4 4.2 3.5-2.8 4.2-1.8-1.2 1.6 14.5H7l1.6-14.5-1.8 1.2L4 11l4.2-3.5z" fill="currentColor"/>
    <path d="M12 4l4 6.8L20 4" fill="none" stroke="${W}" stroke-width="1.6" stroke-linejoin="round"/>`,
  amulet: `
    <path d="M6.5 3.5c.8 6.5 4.8 10.5 9.5 11.5 4.7-1 8.7-5 9.5-11.5" fill="none" stroke="currentColor" stroke-width="2" stroke-dasharray="2.2 1.6"/>
    <polygon points="16,13.5 22.5,20 16,29 9.5,20" fill="currentColor"/>
    <polygon points="16,16 19.2,20 16,25.5" fill="${W}" opacity=".8"/>`,
  ring: `
    <circle cx="16" cy="19.5" r="8.2" fill="none" stroke="currentColor" stroke-width="3.4"/>
    <polygon points="16,2.5 21,7 16,12 11,7" fill="currentColor"/>
    <polygon points="16,4.2 18.5,7 16,9.8" fill="${W}" opacity=".85"/>`,
  boots: `
    <path d="M9.5 4.5h8.5v11l7 3.8a3.6 3.6 0 0 1 1.8 3.2v3H7.5v-4.2l2-3z" fill="currentColor"/>
    <path d="M7.5 28h19.5" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/>`,
};

// ======================= 24 Unique Gear Base Illustrations =======================
const GEAR_ITEM_ICONS: Record<string, string> = {
  // --- 4 Staffs ---
  // Charred dragon-claw staff clutching a blazing magma orb
  ember_staff: `
    <path d="M4.5 27.5L19 13" stroke="${INK}" stroke-width="5.2" stroke-linecap="round"/>
    <path d="M4.5 27.5L19 13" stroke="${WOOD}" stroke-width="3" stroke-linecap="round"/>
    <path d="M9 21l2 2M13.5 16.5l2 2" stroke="${GOLD}" stroke-width="2.2" stroke-linecap="round"/>
    <path d="M16 12.5c-2.5-4.5 0-9.5 5.5-10.5-1.5 2.5-1 4.5 1 6 1.5 2 3.5 2.5 6 1-1 5.5-6 8-12.5 3.5z" fill="${INK}" stroke="${INK}" stroke-width="1.8" stroke-linejoin="round"/>
    <circle cx="22.5" cy="9.5" r="5" fill="${EMBER}" stroke="${INK}" stroke-width="1.4"/>
    <circle cx="22.5" cy="9.5" r="2.8" fill="${GOLD}"/>
    <circle cx="21.5" cy="8.5" r="1.2" fill="${CREAM}"/>
    <path d="M16.5 12c-1.8-3.5.2-7.5 4.5-8.5M20 15.5c3.5 1.8 7.5-.2 8.5-4.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>`,

  // Living twisted briar-root wand with emerald thorns and sylvan amber blossom
  thorn_wand: `
    <path d="M5 27c4-3 6-7.5 8.5-11.5S18 9 22 6.5" fill="none" stroke="${INK}" stroke-width="5" stroke-linecap="round"/>
    <path d="M5 27c4-3 6-7.5 8.5-11.5S18 9 22 6.5" fill="none" stroke="#3b6e38" stroke-width="2.8" stroke-linecap="round"/>
    <path d="M9 22l-3.5-1.5 2.5-2.5M14 16l3.5 1.5-1.5-3.5M11 14l-3-2 3.5-1" fill="${INK}"/>
    <path d="M18 11c-2-3.5-.5-7.5 3.5-8.5 4 1 5.5 5 3.5 8.5-2 2-5 2-7 0z" fill="${INK}" stroke="${INK}" stroke-width="1.6"/>
    <path d="M21.5 3.5c3 1 4.2 4.2 2.6 7-1.6 1.6-3.6 1.6-5.2 0-1.6-2.8-.4-6 2.6-7z" fill="${VERDANT}"/>
    <circle cx="21.5" cy="7.5" r="2.2" fill="${CREAM}"/>
    <path d="M15.5 10.5c-3.5-.5-5.5 1.5-5 4.5 3 .5 5-1.5 5-4.5zM21.5 14.5c.5 3.5-1.5 5.5-4.5 5-.5-3 1.5-5 4.5-5z" fill="currentColor" stroke="${INK}" stroke-width="1.2"/>`,

  // Twin-pronged bronze tuning-fork scepter crackling with cyan lightning
  storm_rod: `
    <path d="M4.5 27.5L16.5 15.5" stroke="${INK}" stroke-width="5" stroke-linecap="round"/>
    <path d="M4.5 27.5L16.5 15.5" stroke="${STEEL_DARK}" stroke-width="2.8" stroke-linecap="round"/>
    <path d="M13.5 12.5l6 6" stroke="${GOLD}" stroke-width="3.2" stroke-linecap="round"/>
    <path d="M14.5 14l4.5-11.5L22 5l-4.5 9.5 9.5-4.5 2.5 3-11.5 4.5z" fill="${INK}" stroke="${INK}" stroke-width="1.8" stroke-linejoin="round"/>
    <path d="M15.2 13.8L19.4 3.5l1.6 1.6-4.2 9.2 9.2-4.2 1.6 1.6-10.3 4.2z" fill="currentColor"/>
    <path d="M20 5.5l2.5 3.5-3 1 3.5 2.5" fill="none" stroke="${W}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="23.5" cy="8.5" r="2" fill="${STORM}"/>`,

  // Obsidian crescent-horned ritual scepter cradling a dark void singularity
  void_scepter: `
    <path d="M4.5 27.5L17.5 14.5" stroke="${INK}" stroke-width="5" stroke-linecap="round"/>
    <path d="M4.5 27.5L17.5 14.5" stroke="#3b2654" stroke-width="2.8" stroke-linecap="round"/>
    <path d="M15 16.5a8.5 8.5 0 1 1 2-2" fill="none" stroke="${INK}" stroke-width="4.4" stroke-linecap="round"/>
    <path d="M15 16.5A7.8 7.8 0 1 1 28.5 7c-2.5-.5-4.5.5-5.5 2.5 2 1 3 3 2.5 5.5A7.8 7.8 0 0 1 15 16.5z" fill="currentColor" stroke="${INK}" stroke-width="1.5" stroke-linejoin="round"/>
    <circle cx="22" cy="10" r="4.2" fill="${INK}" stroke="${VOID}" stroke-width="1.5"/>
    <circle cx="22" cy="10" r="1.8" fill="${CREAM}"/>`,

  // --- 4 Hats ---
  // Crooked archmage spire hat with swept brim and golden buckle
  pointed_hat: `
    <path d="M25 4c-5 1.5-9.5 5-12.5 11L9 20.5 2.5 24c7 4.2 20 4.2 27 0L23 20.5 21.5 13c1.2-3 2.5-5 5.5-5.5z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M24.5 5c-4.5 1.5-8.5 4.8-11 10.2L10 20.5l-5.5 3c6.2 3.4 18.8 3.4 25 0l-5.5-3-1.5-7.8c1-2.6 2.2-4.2 4.8-4.8z" fill="currentColor"/>
    <path d="M9.5 19.2c4.2 1.5 8.8 1.5 13 0l.6 2.4c-4.6 1.6-9.6 1.6-14.2 0z" fill="${RUBY}" stroke="${INK}" stroke-width="1"/>
    <rect x="14" y="18.6" width="4" height="3.6" rx=".6" fill="none" stroke="${GOLD}" stroke-width="1.6"/>`,

  // Deep wind-swept tempest hood with lightning brow-trim and glowing eyes
  storm_hood: `
    <path d="M16 3C9.5 3 5.5 8.5 5 16l-2 9.5c4.5 2.5 9 3.5 13 3.5s8.5-1 13-3.5L27 16C26.5 8.5 22.5 3 16 3z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M16 4.2c-5.8 0-9.4 4.8-9.8 11.8L4.5 24.5c4 2.2 8 3.2 11.5 3.2s7.5-1 11.5-3.2L25.8 16C25.4 9 21.8 4.2 16 4.2z" fill="currentColor"/>
    <path d="M16 10.5L10 16v9.5l6 2.2 6-2.2V16z" fill="${INK}"/>
    <path d="M16 4.2L12.5 10 16 12.5 19.5 10z" fill="${GOLD}"/>
    <path d="M11.5 17.5l2.8 1.2-2.8.8zM20.5 17.5l-2.8 1.2 2.8.8z" fill="${STORM}"/>`,

  // Forged five-spire flame crown set with a blazing central ruby
  ember_circlet: `
    <path d="M4 22L2.5 10.5l6 5L12 7l4 6.5L20 7l3.5 8.5 6-5L28 22c-7.5 3.2-16.5 3.2-24 0z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M5 21.2L3.8 12l5.2 4.4L12.2 8.8l3.8 6.2 3.8-6.2 3.2 7.6L28.2 12 27 21.2c-7 2.8-15 2.8-22 0z" fill="currentColor"/>
    <path d="M5 19.5c7 2.5 15 2.5 22 0v2c-7 2.5-15 2.5-22 0z" fill="${GOLD_DARK}"/>
    <polygon points="16,12 19,17 16,22 13,17" fill="${INK}"/>
    <polygon points="16,13.2 18,17 16,20.8 14,17" fill="${RUBY}"/>
    <polygon points="16,14 17,17 16,19" fill="${CREAM}" opacity=".8"/>`,

  // Velvet academic mortarboard/tam with quill brooch and hanging gold tassel
  scholar_cap: `
    <polygon points="16,4.5 30,11.5 16,18.5 2,11.5" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M7.5 15v7.5c0 2.8 3.8 4.8 8.5 4.8s8.5-2 8.5-4.8V15" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M8.5 15v7c0 2.2 3.4 3.8 7.5 3.8s7.5-1.6 7.5-3.8v-7z" fill="currentColor"/>
    <polygon points="16,5.8 28.2,11.5 16,17.2 3.8,11.5" fill="currentColor"/>
    <polygon points="16,7.2 25.5,11.5 16,15.8 6.5,11.5" fill="#1b2b22" opacity=".45"/>
    <path d="M16 11.5l9 4v7.5" fill="none" stroke="${GOLD}" stroke-width="2" stroke-linecap="round"/>
    <circle cx="16" cy="11.5" r="2" fill="${GOLD}"/>
    <path d="M23.5 23h3l.8 4h-4.6z" fill="${GOLD}"/>`,

  // --- 4 Robes ---
  // High-collared archmage vestment with rune-inscribed stole and pauldrons
  warded_robe: `
    <path d="M10.5 3h11l3 4 5.5 3.5-3 5-2.5-1.5 2 15H5.5l2-15L5 15.5l-3-5L7.5 7z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M11.2 4.2h9.6l2.5 3.5 4.8 3-2.2 3.6-2.8-1.6 2 15.2H6.9l2-15.2-2.8 1.6-2.2-3.6 4.8-3z" fill="currentColor"/>
    <polygon points="12.5,4.2 16,11.5 19.5,4.2" fill="${INK}"/>
    <path d="M13 11v17h6V11l-3 3z" fill="${GOLD}" opacity=".9"/>
    <path d="M16 14v11M14.5 17h3M14.5 21h3" stroke="${INK}" stroke-width="1.4" stroke-linecap="round"/>
    <path d="M8.5 16.5h15" stroke="${CREAM}" stroke-width="1.8"/>`,

  // Druidic mantle of overlapping leaf-scales with bark shoulder-guards
  moss_robe: `
    <path d="M9.5 4h13l5.5 5.5-2.5 5.5-2.5-1 2 14.5c-5.5 1.5-12.5 1.5-18 0L9 14l-2.5 1L4 9.5z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M10.5 5.2h11l4.5 4.5-1.8 4-2.8-1.2 1.8 15c-4.8 1.2-10.8 1.2-15.6 0l1.8-15-2.8 1.2-1.8-4z" fill="currentColor"/>
    <path d="M6 9.5c3 2.5 6.5 3.5 10 3.5s7-1 10-3.5L22 5.2H10z" fill="#296e40"/>
    <path d="M10 17c2 2 4 2 6 0 2 2 4 2 6 0M9.5 22c2.2 2 4.3 2 6.5 0 2.2 2 4.3 2 6.5 0" fill="none" stroke="${CREAM}" stroke-width="1.4" stroke-linecap="round"/>
    <circle cx="16" cy="12.5" r="2.4" fill="${GOLD}" stroke="${INK}" stroke-width="1.2"/>`,

  // Heavy riveted steel plate cuirass with spiked pauldrons and breastplate ridge
  iron_mantle: `
    <path d="M3 7l6-3.5h14L29 7l-2.5 6.5-2.5-1v11L16 29.5 8 23.5v-11l-2.5 1z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M4.5 7.6L9.5 4.8h13l5 2.8-1.8 4.6-3-1.2v11.8L16 28l-6.7-5.2V11l-3 1.2z" fill="currentColor"/>
    <polygon points="12,4.8 16,9.5 20,4.8" fill="${INK}"/>
    <path d="M16 9.5V28M9.5 15.5L16 18.5l6.5-3" fill="none" stroke="${W}" stroke-width="1.6" stroke-linecap="round"/>
    <circle cx="11.5" cy="11.5" r="1.2" fill="${CREAM}"/>
    <circle cx="20.5" cy="11.5" r="1.2" fill="${CREAM}"/>`,

  // Tattered phantom-assassin shroud with high collar and twin crimson clasps
  shadow_cloak: `
    <path d="M9 2.5l7 2 7-2 2.5 5 4 19-4.5-2.5-3 5-3.5-3.5L16 29.5l-2.5-4-3.5 3.5-3-5L2.5 26.5l4-19z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M10 3.8l6 1.8 6-1.8 2 4.2 3.5 16.8-3.5-2-2.6 4.2-3-3L16 27.5l-2.4-3.5-3 3-2.6-4.2-3.5 2L8 8z" fill="currentColor"/>
    <polygon points="11.5,5.5 16,13.5 20.5,5.5 16,7" fill="${INK}"/>
    <circle cx="12.5" cy="10.5" r="1.6" fill="${RUBY}"/>
    <circle cx="19.5" cy="10.5" r="1.6" fill="${RUBY}"/>
    <path d="M12.5 10.5h7" stroke="${GOLD}" stroke-width="1.4"/>`,

  // --- 4 Amulets ---
  // Faceted teardrop mana-vial on a silver chain with corked gold cap
  glass_charm: `
    <path d="M8 3.5c1 5 4 8 8 8s7-3 8-8" fill="none" stroke="${INK}" stroke-width="3.2" stroke-linecap="round"/>
    <path d="M8 3.5c1 5 4 8 8 8s7-3 8-8" fill="none" stroke="${STEEL}" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M13 9.5h6v3.5l5.5 7.5a7.2 7.2 0 1 1-17 0L13 13z" fill="${INK}" stroke="${INK}" stroke-width="1.8" stroke-linejoin="round"/>
    <rect x="13.5" y="10" width="5" height="3" rx=".8" fill="${GOLD}"/>
    <path d="M14 13h4l4.8 6.8a6 6 0 1 1-13.6 0z" fill="currentColor"/>
    <path d="M10.5 21.5c2-1 4-1 5.5 0s3.5 1 5.5 0a5.2 5.2 0 1 1-11 0z" fill="${STORM}" opacity=".85"/>
    <path d="M13 16l-1.8 4.5" stroke="${W}" stroke-width="1.5" stroke-linecap="round"/>`,

  // Winged chronos sandglass pendant on a chain
  hourglass_pendant: `
    <path d="M8.5 3c1 4.5 3.8 7 7.5 7s6.5-2.5 7.5-7" fill="none" stroke="${INK}" stroke-width="3" stroke-linecap="round"/>
    <path d="M8.5 3c1 4.5 3.8 7 7.5 7s6.5-2.5 7.5-7" fill="none" stroke="${GOLD}" stroke-width="1.4" stroke-linecap="round"/>
    <path d="M10 10h12l-3.8 8.5L22 27H10l3.8-8.5z" fill="${INK}" stroke="${INK}" stroke-width="2.2" stroke-linejoin="round"/>
    <path d="M4 13l6.5 3.5-1.5 4L3 17.5zM28 13l-6.5 3.5 1.5 4 6-3z" fill="${INK}" stroke="${INK}" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M4.8 13.8l5.2 2.8-1 2.8-5-2.5zM27.2 13.8l-5.2 2.8 1 2.8 5-2.5z" fill="${CREAM}"/>
    <polygon points="11.2,11.2 20.8,11.2 16,18.5" fill="currentColor"/>
    <polygon points="16,18.5 20.8,25.8 11.2,25.8" fill="currentColor"/>
    <polygon points="13,24.8 19,24.8 16,20.5" fill="${GOLD}"/>
    <rect x="9.5" y="9.5" width="13" height="2" rx=".8" fill="${GOLD}"/>
    <rect x="9.5" y="25.5" width="13" height="2" rx=".8" fill="${GOLD}"/>`,

  // Hinged emerald-and-gold oval locket engraved with a glowing world-tree leaf
  sage_locket: `
    <path d="M8 3.5c1 4.8 4 7.8 8 7.8s7-3 8-7.8" fill="none" stroke="${INK}" stroke-width="3" stroke-linecap="round"/>
    <path d="M8 3.5c1 4.8 4 7.8 8 7.8s7-3 8-7.8" fill="none" stroke="${GOLD}" stroke-width="1.4" stroke-linecap="round"/>
    <ellipse cx="16" cy="19.5" rx="8.5" ry="10" fill="${INK}"/>
    <ellipse cx="16" cy="19.5" rx="7.2" ry="8.6" fill="${GOLD}"/>
    <ellipse cx="16" cy="19.5" rx="5.4" ry="6.8" fill="currentColor"/>
    <path d="M16 14.5c2.5 2 3.2 4.8 1.8 7.5-1.4 1.4-2.2 1.4-3.6 0-1.4-2.7-.7-5.5 1.8-7.5z" fill="${VERDANT}" stroke="${INK}" stroke-width="1"/>
    <path d="M16 16.5v6.5" stroke="${CREAM}" stroke-width="1.3" stroke-linecap="round"/>`,

  // Eight-point celestial astrolabe medallion with blazing supernova core
  star_talisman: `
    <path d="M9 3c1 4.5 3.5 7 7 7s6-2.5 7-7" fill="none" stroke="${INK}" stroke-width="3" stroke-linecap="round"/>
    <path d="M9 3c1 4.5 3.5 7 7 7s6-2.5 7-7" fill="none" stroke="${GOLD}" stroke-width="1.4" stroke-linecap="round"/>
    <path d="M16 8l3 5.5 5.5-2-2 5.5 5.5 3-5.5 3 2 5.5-5.5-2-3 5.5-3-5.5-5.5 2 2-5.5-5.5-3 5.5-3-2-5.5 5.5 2z" fill="${INK}" stroke="${INK}" stroke-width="1.6" stroke-linejoin="round"/>
    <path d="M16 9.5l2.4 4.8 4.8-1.6-1.6 4.8 4.8 2.5-4.8 2.5 1.6 4.8-4.8-1.6L16 30.5l-2.4-4.8-4.8 1.6 1.6-4.8L5.6 20l4.8-2.5-1.6-4.8 4.8 1.6z" fill="currentColor"/>
    <circle cx="16" cy="20" r="4.5" fill="${INK}"/>
    <circle cx="16" cy="20" r="3.2" fill="${CREAM}"/>`,

  // --- 4 Rings ---
  // Heavy solar-gold signet ring crowned with a blazing sun crest
  sun_ring: `
    <circle cx="16" cy="19.5" r="8.5" fill="none" stroke="${INK}" stroke-width="5.8"/>
    <circle cx="16" cy="19.5" r="8.5" fill="none" stroke="currentColor" stroke-width="3.2"/>
    <path d="M16 2.5l2.2 3.8 4-1.5-1.2 4.2 4 2-4 2 1.2 4.2-4-1.5L16 19.5l-2.2-3.8-4 1.5 1.2-4.2-4-2 4-2-1.2-4.2 4 1.5z" fill="${INK}" stroke="${INK}" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M16 3.8l1.8 3.2 3.4-1.2-1 3.4 3.4 1.8-3.4 1.8 1 3.4-3.4-1.2L16 18.2l-1.8-3.2-3.4 1.2 1-3.4-3.4-1.8 3.4-1.8-1-3.4 3.4 1.2z" fill="${GOLD}"/>
    <circle cx="16" cy="11" r="3.2" fill="${EMBER}" stroke="${INK}" stroke-width="1.2"/>
    <circle cx="15.2" cy="10.2" r="1.1" fill="${CREAM}"/>`,

  // Split horseshoe-magnet iron ring with glowing polarized tips and arc
  lode_band: `
    <path d="M11 8.5a9.5 9.5 0 1 0 10 0" fill="none" stroke="${INK}" stroke-width="6.2" stroke-linecap="round"/>
    <path d="M11 8.5a9.5 9.5 0 1 0 10 0" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round"/>
    <rect x="8.5" y="5.5" width="4.8" height="4.8" rx="1" fill="${RUBY}" stroke="${INK}" stroke-width="1.4"/>
    <rect x="18.7" y="5.5" width="4.8" height="4.8" rx="1" fill="${STORM}" stroke="${INK}" stroke-width="1.4"/>
    <path d="M13.5 6.5l2.5-3 2.5 3" fill="none" stroke="${CREAM}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>`,

  // Opulent braided gold ring set with three royal crown-gems
  gilded_ring: `
    <ellipse cx="16" cy="20" rx="9.5" ry="7.8" fill="none" stroke="${INK}" stroke-width="6"/>
    <ellipse cx="16" cy="20" rx="9.5" ry="7.8" fill="none" stroke="${GOLD}" stroke-width="3.4"/>
    <path d="M6.5 13L9 6.5l4.5 3.5L16 4l2.5 6L23 6.5 25.5 13z" fill="${INK}" stroke="${INK}" stroke-width="1.6" stroke-linejoin="round"/>
    <path d="M7.5 12.5L9.5 7.8l4 3L16 5.2l2.5 5.6 4-3 2 4.7z" fill="currentColor"/>
    <polygon points="16,6.8 18.2,10.5 16,14.2 13.8,10.5" fill="${RUBY}"/>
    <circle cx="10.2" cy="11" r="1.6" fill="${VERDANT}"/>
    <circle cx="21.8" cy="11" r="1.6" fill="${VERDANT}"/>`,

  // Sculpted silver falcon-wing ring gripping a sky-sapphire crystal
  hawk_ring: `
    <circle cx="16" cy="20" r="8" fill="none" stroke="${INK}" stroke-width="5.6"/>
    <circle cx="16" cy="20" r="8" fill="none" stroke="${STEEL}" stroke-width="3"/>
    <path d="M3 7.5c4.5 0 8 2 10.5 5.5h5C21 9.5 24.5 7.5 29 7.5c-2 4-5 6.8-9 8L16 18l-4-2.5c-4-1.2-7-4-9-8z" fill="${INK}" stroke="${INK}" stroke-width="1.6" stroke-linejoin="round"/>
    <path d="M4.5 8.5c3.8.2 6.8 1.8 9 4.8h5c2.2-3 5.2-4.6 9-4.8-1.8 3.2-4.2 5.4-7.8 6.4L16 17l-3.7-2.1C8.7 13.9 6.3 11.7 4.5 8.5z" fill="currentColor"/>
    <polygon points="16,3.5 19.5,9.5 16,15.5 12.5,9.5" fill="${INK}"/>
    <polygon points="16,4.8 18.4,9.5 16,14.2 13.6,9.5" fill="${STORM}"/>
    <polygon points="16,5.5 17.4,9.5 16,12" fill="${W}" opacity=".85"/>`,

  // --- 4 Boots ---
  // Sleek sky-strider greaves with swept golden Valkyrie wings at the ankle
  wind_striders: `
    <path d="M9 5.5h7.5v10.5l9 4.5a3.5 3.5 0 0 1 2 3.2V27H7v-5l2-3z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M10 6.5h5.5v10l8.8 4.4c.8.4 1.4 1.2 1.4 2.2V26H8v-3.5l2-2.8z" fill="currentColor"/>
    <path d="M3 10.5c4.5-3 9.5-3 13.5 0-3 1.2-5 2.8-6 4.8 2.2-.2 4.2.2 5.5 1.2-3.5.8-7 2.2-10 4.5l1.5-5-4.5-1.5z" fill="${INK}" stroke="${INK}" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M4.2 11c3.8-2.2 8-2.2 11.2 0-2.5 1-4.2 2.4-5 4.2 1.8-.2 3.4.2 4.5 1-3 .6-5.8 1.8-8.2 3.6l1.2-4.2-3.7-1.2z" fill="${CREAM}"/>
    <rect x="7" y="25.5" width="19.5" height="2.2" rx="1" fill="${GOLD}"/>`,

  // Heavy articulated dreadnought sabatons with overlapping steel plates and knee spike
  iron_greaves: `
    <path d="M7.5 4l9 1.5 2.5 3.5-2 3.5v4l9 4.2 2 4.8v2.5H6.5v-6l1.5-2.5V8L6 5.5z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M8.5 5.2l7.5 1.2 2 2.6-1.8 3v4.5l8.8 4 1.6 3.8v2.2H7.5v-4.8l1.5-2.2V7.8z" fill="currentColor"/>
    <path d="M9 11.5h7.5M9 15.5h7.5M14.5 19.5l3.5 6.5M19.5 21l2.5 5" stroke="${INK}" stroke-width="1.8" stroke-linecap="round"/>
    <polygon points="16,6 20.5,9 16,12" fill="${STEEL}"/>
    <rect x="6.5" y="26" width="21" height="2.5" rx=".8" fill="${STEEL_DARK}"/>`,

  // Blackened volcanic warboots with glowing magma cracks and flame heel-spur
  ember_treads: `
    <path d="M9.5 5h8v10.5l8.8 4.5a3.5 3.5 0 0 1 1.8 3.2V27H7.5v-4.5L3 19.5l5-1.5 1.5-2.5z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M10.5 6h6v10l8.5 4.2c.8.4 1.4 1.2 1.4 2.2V26H8.5v-3.8l-4-2.4 4-1.2 2-2.8z" fill="#2b1b24"/>
    <path d="M11.5 8.5l3.5 3-2 4.5 5 3.5-1.5 4.5M16 19l5.5 2.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M8.5 18l-5 1.5 4.5 3c-1.5 1-3 1.2-4.5.8 2 1.8 4.5 2.2 7 .5z" fill="${EMBER}"/>
    <rect x="7.5" y="25.5" width="19.5" height="2.4" rx="1" fill="${EMBER}"/>`,

  // Sylvan bark-and-vine striders with sprouting glowing leaves
  moss_sandals: `
    <path d="M9.5 5.5h7.5v10.5l8.8 4.5a3.5 3.5 0 0 1 1.8 3V27H7.5v-5l2-3z" fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M10.5 6.5h5.5V16l8.5 4.4c.8.4 1.4 1.2 1.4 2.2V26H8.5v-3.8l2-2.6z" fill="${WOOD}"/>
    <path d="M10 9.5l6.5 2M10 13.5l6.5 2M13 18.5l5 6.5M18 20.5l4 4.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
    <path d="M9.5 11.5c-3.5-1.5-6 0-6.5 3.5 3.5 1 6-.5 6.5-3.5zM9.5 16.5c-3-1-5 .5-5 3 2.8.8 4.8-.5 5-3z" fill="${VERDANT}" stroke="${INK}" stroke-width="1.2"/>
    <rect x="7.5" y="25.5" width="19.5" height="2.4" rx="1" fill="#3b6e38"/>`,
};

// ======================= 3 Distinct Chest Illustrations =======================
export function chestArt(id: ChestId): string {
  if (id === 'bronze') {
    // Rugged iron-strapped oak field strongbox with plank lines, corner brackets & heavy padlock
    return `<svg class="chest-svg" viewBox="0 0 120 100" aria-hidden="true">
      <ellipse cx="60" cy="56" rx="48" ry="34" fill="#ff9d5c" opacity=".16"/>
      <ellipse cx="60" cy="88" rx="44" ry="6" fill="#05060b" opacity=".6"/>
      <rect x="18" y="44" width="84" height="40" rx="4" fill="#4f2b17" stroke="${INK}" stroke-width="3"/>
      <path d="M22 26h76l6 18H16z" fill="#6e3d22" stroke="${INK}" stroke-width="3" stroke-linejoin="round"/>
      <path d="M20 57h80M20 70h80" stroke="#2d170b" stroke-width="2"/>
      <rect x="28" y="26" width="10" height="58" fill="#c98349" stroke="${INK}" stroke-width="2"/>
      <rect x="82" y="26" width="10" height="58" fill="#c98349" stroke="${INK}" stroke-width="2"/>
      <rect x="15" y="41" width="90" height="7" rx="2" fill="#d9955b" stroke="${INK}" stroke-width="2"/>
      <circle cx="33" cy="33" r="1.8" fill="#ffe0b5"/><circle cx="87" cy="33" r="1.8" fill="#ffe0b5"/>
      <circle cx="33" cy="52" r="1.8" fill="#ffe0b5"/><circle cx="87" cy="52" r="1.8" fill="#ffe0b5"/>
      <circle cx="33" cy="76" r="1.8" fill="#ffe0b5"/><circle cx="87" cy="76" r="1.8" fill="#ffe0b5"/>
      <rect x="51" y="40" width="18" height="22" rx="3" fill="#e09b5c" stroke="${INK}" stroke-width="2.2"/>
      <circle cx="60" cy="49" r="3" fill="${INK}"/>
      <path d="M58.5 50l-1.5 7h6l-1.5-7z" fill="${INK}"/>
    </svg>`;
  }
  if (id === 'silver') {
    // Arched Gothic moon-silver reliquary coffer with winged ribs and an amethyst star-seal
    return `<svg class="chest-svg" viewBox="0 0 120 100" aria-hidden="true">
      <ellipse cx="60" cy="54" rx="50" ry="36" fill="#82b7ff" opacity=".2"/>
      <ellipse cx="60" cy="88" rx="44" ry="6" fill="#05060b" opacity=".6"/>
      <path d="M18 46L10 38l4 14 4-2zM102 46l8-8-4 14-4-2z" fill="#b9c8e6" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
      <rect x="20" y="45" width="80" height="39" rx="6" fill="#25314d" stroke="${INK}" stroke-width="3"/>
      <path d="M20 45c0-20 18-30 40-30s40 10 40 30z" fill="#3a4c73" stroke="${INK}" stroke-width="3" stroke-linejoin="round"/>
      <path d="M36 19v65M84 19v65" stroke="#c5d4f2" stroke-width="7" stroke-linecap="round"/>
      <path d="M36 19v65M84 19v65" stroke="#eef4ff" stroke-width="2" stroke-linecap="round"/>
      <rect x="17" y="42" width="86" height="7" rx="3" fill="#c5d4f2" stroke="${INK}" stroke-width="2"/>
      <polygon points="60,29 73,45 60,65 47,45" fill="#d8e4ff" stroke="${INK}" stroke-width="2.2" stroke-linejoin="round"/>
      <polygon points="60,34 68,45 60,59 52,45" fill="#b77bff"/>
      <polygon points="60,36 64,45 60,54" fill="${W}" opacity=".8"/>
    </svg>`;
  }
  // Gold: Grand imperial dragon-crowned treasure coffer with crimson panels, claw feet & ruby sunburst
  return `<svg class="chest-svg" viewBox="0 0 120 100" aria-hidden="true">
    <ellipse cx="60" cy="52" rx="54" ry="40" fill="#ffd24d" opacity=".24"/>
    <ellipse cx="60" cy="89" rx="46" ry="6" fill="#05060b" opacity=".65"/>
    <path d="M22 82l-6 7h12zM98 82l6 7H92z" fill="#f5b82e" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <path d="M18 28l-8-10 4 18zM102 28l8-10-4 18zM60 10l-6 8h12z" fill="#ffd86b" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
    <rect x="18" y="44" width="84" height="40" rx="6" fill="#6b1529" stroke="${INK}" stroke-width="3"/>
    <path d="M18 44c0-19 18-28 42-28s42 9 42 28z" fill="#8f1d38" stroke="${INK}" stroke-width="3" stroke-linejoin="round"/>
    <rect x="29" y="19" width="11" height="65" rx="2" fill="#f5b82e" stroke="${INK}" stroke-width="2"/>
    <rect x="80" y="19" width="11" height="65" rx="2" fill="#f5b82e" stroke="${INK}" stroke-width="2"/>
    <rect x="15" y="41" width="90" height="8" rx="3" fill="#ffd24d" stroke="${INK}" stroke-width="2"/>
    <path d="M46 34h28l4 14-18 18-18-18z" fill="#ffd86b" stroke="${INK}" stroke-width="2.2" stroke-linejoin="round"/>
    <polygon points="60,38 69,48 60,60 51,48" fill="${RUBY}" stroke="${INK}" stroke-width="1.5"/>
    <polygon points="60,40 65,48 60,55" fill="#fff0a6" opacity=".85"/>
  </svg>`;
}

// Grimoire upgrades share the passive with the same effect.
const SHOP_TO_ICON: Record<string, string> = {
  might: 'might', vitality: 'heart', haste: 'quickening', armor: 'iron', regen: 'bloom', speed: 'boots',
  magnet: 'lode', growth: 'tome', greed: 'fortune', area: 'expansion', reroll: 'reroll', revive: 'revive',
};

const svg = (body: string | undefined, fallback: string) =>
  body ? `<svg class="sicon" viewBox="0 0 32 32" aria-hidden="true">${body}</svg>` : fallback;

export function spellIcon(id: string, fallback = ''): string {
  return svg(ICONS[id], fallback);
}

export function passiveIcon(id: string, fallback = ''): string {
  return svg(PASSIVE_ICONS[id], fallback);
}

export function gearIcon(slot: GearSlot): string {
  return svg(GEAR_ICONS[slot], '');
}

export function gearItemIcon(baseId: string): string {
  const body = GEAR_ITEM_ICONS[baseId];
  if (body) return svg(body, '');
  const base = gearBase(baseId);
  return base ? gearIcon(base.slot) : '';
}

export function shopIcon(id: string, fallback = ''): string {
  return svg(PASSIVE_ICONS[SHOP_TO_ICON[id]], fallback);
}

export const SPELL_ICON_IDS = Object.keys(ICONS);
export const PASSIVE_ICON_IDS = Object.keys(PASSIVE_ICONS);
export const GEAR_ITEM_ICON_IDS = Object.keys(GEAR_ITEM_ICONS);
